import { call, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import axios from 'axios';
import moment from 'moment';
import { updatePaginatedGuestInsightFilters } from './actions';

const apiRoot = `${window._env_?.REACT_APP_SCHEMA_API_PATH}`;

const getGuestInsightsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/guest-insight`;

export const getPaginatedGuestInsightsAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getGuestInsightsApiRoot(
        tenantId,
        businessContextId
      )}/search?pageNumber=${urlParams.index}&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then((response) => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch((error) => {
      throw error;
    });
};

export const getCurrentGuestDetailAsync = (
  tenantId,
  businessContextId,
  bodyParams
) => {
  return axios
    .post(
      `${getGuestInsightsApiRoot(tenantId, businessContextId)}/get/bulk`,
      bodyParams
    )
    .then((response) => ({
      data: response.data
    }))
    .catch((error) => {
      throw error;
    });
};

// selectors
const currentTenantId = (store) => store.user.selectedTenant.tenantId;
const currentBusinessContextId = (store) =>
  store.user.selectedBusinessContext.businessContextId;
const pageNo = (store) =>
  store.guestInsights ? store.guestInsights.filters?.pageNo : 0;
const rowsPerPage = (store) =>
  store.guestInsights.filters ? store.guestInsights.filters?.rowsPerPage : 10;
const paginatedGuestInsightsList = (store) =>
  store.guestInsights ? store.guestInsights.paginatedList : [];
const currentGuestDetail = (store) =>
  store.guestInsights ? store.guestInsights.current : {};
const filterGuestInfoValues = (store) =>
  store.guestInsights.filters
    ? store.guestInsights.filters?.filterGuestInfoValues
    : [{}];
const filterContactInfoValues = (store) =>
  store.guestInsights.filters
    ? store.guestInsights.filters?.filterContactInfoValues
    : [{}];
const filterConsentValues = (store) =>
  store.guestInsights.filters
    ? store.guestInsights.filters?.filterConsentValues
    : [{}];
const filterDateValues = (store) =>
  store.guestInsights.filters?.filterDateValues ?? [];

const isValidRangeEnd = (endDate, startDate) =>
  endDate &&
  (startDate
    ? moment(endDate)
        .startOf('day')
        .isSameOrAfter(moment(startDate).startOf('day'))
    : true);

const toUtcStartOfDay = (date) =>
  date ? moment(date).startOf('day').utc().toISOString() : null;

const toUtcEndOfDay = (date) =>
  date ? moment(date).endOf('day').utc().toISOString() : null;

const getLocalParams = (requestParams) => {
  let transformedParams = {};
  for (let param in requestParams) {
    if (param === 'guestInfo' && requestParams[param].length > 0) {
      let GuestInfoValues = requestParams[param].reduce((acc, item) => {
        let value;
        if (item.dataType === 'String') {
          value = item.regex.trim();
        } else {
          value = item.regex;
        }
        acc[item.value] = value;
        return acc;
      }, {});
      for (let key in GuestInfoValues) {
        if (!GuestInfoValues[key]) {
          delete GuestInfoValues[key];
        } else {
          transformedParams[key] = GuestInfoValues[key];
        }
      }
    }
    if (param === 'contactInfo' && requestParams[param].length > 0) {
      let ContactInfoValues = requestParams[param].reduce((acc, item) => {
        let value;
        if (item.dataType === 'String') {
          value = item.regex.trim();
        } else {
          value = item.regex;
        }
        acc[item.value] = value;
        return acc;
      }, {});
      for (let key in ContactInfoValues) {
        if (!ContactInfoValues[key]) {
          delete ContactInfoValues[key];
        } else if (
          key === 'primaryEmailAddress' ||
          key === 'primaryPhoneNumber'
        ) {
          transformedParams[key] = ContactInfoValues[key];
        } else {
          transformedParams['primaryMailingAddress'] = {
            ...transformedParams['primaryMailingAddress'],
            [key]: ContactInfoValues[key]
          };
        }
      }
    }
    if (param === 'consent' && requestParams[param].length > 0) {
      requestParams[param].forEach((consentItem) => {
        if (consentItem.dataType === 'Boolean' && consentItem.regex !== '') {
          if (consentItem?.value === 'emailPromotionalMessaging') {
            transformedParams['consentToEmailPromotionalMessaging'] =
              consentItem.regex === 'true' ? true : false;
          }
          if (consentItem?.value === 'emailTransactionalMessaging') {
            transformedParams['consentToEmailTransactionalMessaging'] =
              consentItem.regex === 'true' ? true : false;
          }
          if (consentItem?.value === 'smsPromotionalMessaging') {
            transformedParams['consentToSmsPromotionalMessaging'] =
              consentItem.regex === 'true' ? true : false;
          }
          if (consentItem?.value === 'smsTransactionalMessaging') {
            transformedParams['consentToSmsTransactionalMessaging'] =
              consentItem.regex === 'true' ? true : false;
          }
        }
      });
    }
    if (
      param === 'birthDateStart' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
    ) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }
    if (
      param === 'birthDateEnd' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day')) &&
      (requestParams['birthDateStart']
        ? moment(requestParams[param])
            .startOf('day')
            .isSameOrAfter(
              moment(requestParams['birthDateStart']).startOf('day')
            )
        : true)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
    if (
      param === 'weddingAnniversaryDateStart' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
    ) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }
    if (
      param === 'weddingAnniversaryDateEnd' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day')) &&
      (requestParams['weddingAnniversaryDateStart']
        ? moment(requestParams[param])
            .startOf('day')
            .isSameOrAfter(
              moment(requestParams['weddingAnniversaryDateStart']).startOf(
                'day'
              )
            )
        : true)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
    if (
      param === 'profileCreatedDateStart' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
    ) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }
    if (
      param === 'profileCreatedDateEnd' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day')) &&
      (requestParams['profileCreatedDateStart']
        ? moment(requestParams[param])
            .startOf('day')
            .isSameOrAfter(
              moment(requestParams['profileCreatedDateStart']).startOf('day')
            )
        : true)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
    // Guest Activity Summary Date Filters
    if (
      param === 'lastStayDateStart' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
    ) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }
    if (
      param === 'lastStayDateEnd' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day')) &&
      (requestParams['lastStayDateStart']
        ? moment(requestParams[param])
            .startOf('day')
            .isSameOrAfter(
              moment(requestParams['lastStayDateStart']).startOf('day')
            )
        : true)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
    if (
      param === 'lastGolfDateStart' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
    ) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }
    if (
      param === 'lastGolfDateEnd' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day')) &&
      (requestParams['lastGolfDateStart']
        ? moment(requestParams[param])
            .startOf('day')
            .isSameOrAfter(
              moment(requestParams['lastGolfDateStart']).startOf('day')
            )
        : true)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
    if (
      param === 'lastSpaDateStart' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
    ) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }
    if (
      param === 'lastSpaDateEnd' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day')) &&
      (requestParams['lastSpaDateStart']
        ? moment(requestParams[param])
            .startOf('day')
            .isSameOrAfter(
              moment(requestParams['lastSpaDateStart']).startOf('day')
            )
        : true)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
    if (
      param === 'lastDiningDateStart' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day'))
    ) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }
    if (
      param === 'lastDiningDateEnd' &&
      requestParams[param] &&
      moment(requestParams[param])
        .startOf('day')
        .isSameOrBefore(moment().startOf('day')) &&
      (requestParams['lastDiningDateStart']
        ? moment(requestParams[param])
            .startOf('day')
            .isSameOrAfter(
              moment(requestParams['lastDiningDateStart']).startOf('day')
            )
        : true)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
    if (param === 'nextStayDateStart' && requestParams[param]) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }

    if (
      param === 'nextStayDateEnd' &&
      isValidRangeEnd(requestParams[param], requestParams.nextStayDateStart)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }

    if (param === 'nextGolfDateStart' && requestParams[param]) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }

    if (
      param === 'nextGolfDateEnd' &&
      isValidRangeEnd(requestParams[param], requestParams.nextGolfDateStart)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }

    if (param === 'nextSpaDateStart' && requestParams[param]) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }

    if (
      param === 'nextSpaDateEnd' &&
      isValidRangeEnd(requestParams[param], requestParams.nextSpaDateStart)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }

    if (param === 'nextDiningDateStart' && requestParams[param]) {
      transformedParams[param] = toUtcStartOfDay(requestParams[param]);
    }

    if (
      param === 'nextDiningDateEnd' &&
      isValidRangeEnd(requestParams[param], requestParams.nextDiningDateStart)
    ) {
      transformedParams[param] = toUtcEndOfDay(requestParams[param]);
    }
  }
  return transformedParams;
};

// Generator functions
export function* getPaginatedGuestInsightsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedGuestInsightsList);

    if (
      action.type === actions.GET_PAGINATED_GUEST_INSIGHTS &&
      (currentList.length === 0 || payload.force === true)
    ) {
      const dateVals = yield select(filterDateValues);
      const getDateField = (value, field) =>
        dateVals.find((o) => o.value === value)?.regex?.[field] || null;

      const requestBody = {
        guestInfo: yield select(filterGuestInfoValues),
        contactInfo: yield select(filterContactInfoValues),
        consent: yield select(filterConsentValues),
        birthDateStart: getDateField('birthDate', 'start'),
        birthDateEnd: getDateField('birthDate', 'end'),
        weddingAnniversaryDateStart: getDateField(
          'weddingAnniversaryDate',
          'start'
        ),
        weddingAnniversaryDateEnd: getDateField(
          'weddingAnniversaryDate',
          'end'
        ),
        profileCreatedDateStart: getDateField('profileCreatedDate', 'start'),
        profileCreatedDateEnd: getDateField('profileCreatedDate', 'end'),
        lastStayDateStart: getDateField('lastStayDate', 'start'),
        lastStayDateEnd: getDateField('lastStayDate', 'end'),
        nextStayDateStart: getDateField('nextStayDate', 'start'),
        nextStayDateEnd: getDateField('nextStayDate', 'end'),
        lastGolfDateStart: getDateField('lastGolfDate', 'start'),
        lastGolfDateEnd: getDateField('lastGolfDate', 'end'),
        nextGolfDateStart: getDateField('nextGolfDate', 'start'),
        nextGolfDateEnd: getDateField('nextGolfDate', 'end'),
        lastSpaDateStart: getDateField('lastSpaDate', 'start'),
        lastSpaDateEnd: getDateField('lastSpaDate', 'end'),
        nextSpaDateStart: getDateField('nextSpaDate', 'start'),
        nextSpaDateEnd: getDateField('nextSpaDate', 'end'),
        lastDiningDateStart: getDateField('lastDiningDate', 'start'),
        lastDiningDateEnd: getDateField('lastDiningDate', 'end'),
        nextDiningDateStart: getDateField('nextDiningDate', 'start'),
        nextDiningDateEnd: getDateField('nextDiningDate', 'end')
      };
      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = { ...getLocalParams(requestBody) };
      currentList = yield call(
        getPaginatedGuestInsightsAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      yield put(
        updatePaginatedGuestInsightFilters({
          totalCount: currentList.totalRecords
        })
      );
    }
    yield put(
      actions.gettingPaginatedGuestInsightsSucceeded(currentList.data || [])
    );
  } catch (ex) {
    yield put(updatePaginatedGuestInsightFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedGuestInsightsFailed(ex));
  }
}

export function* getCurrentGuestDetailIfNeeded(action) {
  const { payload = {} } = action;
  const { data, forEvents } = payload;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentDetail = yield select(currentGuestDetail);

    if (
      action.type === actions.GET_CURRENT_GUEST_DETAIL &&
      currentDetail.guestProfileUuid !== data[0]
    ) {
      let bodyParams = [data[0]];
      currentDetail = yield call(
        getCurrentGuestDetailAsync,
        tenantId,
        businessContextId,
        bodyParams
      );
      yield put(
        actions.gettingCurrentGuestDetailSucceeded(currentDetail.data[0])
      );
      if (forEvents) {
        let modifiedGuestDetail = {
          '': currentDetail.data.map((data) => {
            return {
              label: data.guestInformation.firstName,
              link: `/gcm/guest-insights/details/${data.guestProfileUuid}`
            };
          })
        };
        yield put(actions.updateTransformedGuestDetail(modifiedGuestDetail));
      }
    }
  } catch (ex) {
    yield put(actions.gettingCurrentGuestDetailFailed(ex));
  }
}
